<style>
.border-0 {
  border-radius: 0px !important;
}
</style>
<template>
  <div>
    <!-- Filters. Rebuilt on the shared KarroFin FilterBar: same endpoints, same
         params -- only the presentation moved. These sat permanently open above
         the KPI strip and the table, so the list started well down the page. -->
    <FilterBar
      :show-search="false"
      :show-date="false"
      :advanced-count="activeChips.length"
      :rest-count="restCount"
      @change="getInstallmentList(filter_status, true)"
      @reset="resetFilters"
    >
      <template v-for="f in filterLayout">
        <div
          :key="f.key"
          class="kf-field"
          :class="{ 'kf-field--rest': f.rest }"
        >
          <span class="kf-field__label">{{ f.label }}</span>

          <DateField
            v-if="f.key === 'daterange'"
            v-model="filter.daterange"
            range
            @change="onDateChange()"
          />

          <DateField
            v-else-if="f.key === 'follow_up_date'"
            v-model="filter.follow_up_date"
            @change="onDateChange()"
          />

          <v-select
            v-else-if="f.key === 'call_tag'"
            v-model="filter.call_tag"
            :items="list_of_tags"
            item-text="call_tag"
            item-value="call_tag"
            placeholder="Any tag"
            menu-props="auto"
            dense
            outlined
            single-line
            hide-details
            clearable
            @change="getInstallmentList(filter_status, true)"
            @click="getListOfTags()"
          />

          <v-select
            v-else-if="f.key === 'date_bucket_id'"
            v-model="filter.date_bucket_id"
            :items="overdue_date_list"
            item-text="days_bucket_list"
            item-value="id"
            placeholder="Any bucket"
            menu-props="auto"
            dense
            outlined
            single-line
            hide-details
            clearable
            @change="getInstallmentList(filter_status, true)"
            @click="getOverdueDateList()"
          />

          <v-select
            v-else-if="f.key === 'agent_type'"
            v-model="agent_type"
            :items="agent_type_list"
            item-value="value"
            item-text="text"
            placeholder="Any"
            dense
            outlined
            single-line
            hide-details
            clearable
          />

          <v-combobox
            v-else
            v-model="filters.agent_assigned"
            :items="agent_list"
            :loading="agent_loading"
            :search-input.sync="agent_search"
            item-text="name"
            item-value="id"
            placeholder="Any agent"
            dense
            outlined
            single-line
            hide-details
            clearable
            @focus="getAgentList()"
            @keyup="onAgentSearch()"
            @change="getInstallmentList(filter_status, true)"
          >
            <template v-slot:append-item>
              <div v-intersect="endIntersectAgent"></div>
            </template>
          </v-combobox>
        </div>
      </template>

      <!-- Chips say why the table is short, without the grid having to be open. -->
      <template #chips>
        <v-chip
          v-for="c in activeChips"
          :key="c.key"
          small
          close
          class="kf-fchip"
          @click:close="clearFilter(c.key)"
        >
          <strong>{{ c.label }}:</strong>&nbsp;{{ c.value }}
        </v-chip>
      </template>
    </FilterBar>

    <FilterTab :filter_status="filter_status" :total_due_emi_count="total_due_emi_count"
      :total_over_due_emi_count="total_over_due_emi_count" :total_paid_emi_count="total_paid_emi_count"
      @update="updateFilter" :total_overdue="total_overdue" :upcoming_amount="upcoming_amount"
      :overdue_amount="overdue_amount" :overdue_charge="overdue_charge" :total_paid_amount="total_paid_amount"
      :paid_amount="paid_amount" :paid_charge="paid_charge" :total_overdue_user_count="total_overdue_user_count"
      :total_due_user_count="total_due_user_count" :total_paid_user_count="total_paid_user_count"></FilterTab>
    <v-card flat>
      <v-stepper v-model="step" class="elevation-0">
        <v-stepper-items class="auth-basic">
          <v-stepper-content class="pa-0" step="1">
            <OverDueEmiTable :installment_list="itemsWithSno" :table_loading="table_loading"
              @reload_installments="getInstallmentList"></OverDueEmiTable>
          </v-stepper-content>

          <v-stepper-content class="pa-0" step="2">
            <UpcomingEmiTable :installment_list="itemsWithSno" :table_loading="table_loading"></UpcomingEmiTable>
          </v-stepper-content>

          <v-stepper-content class="pa-0" step="3">
            <PaidEmiTable :installment_list="itemsWithSno" :table_loading="table_loading"></PaidEmiTable>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </v-card>

    <v-pagination v-model="page_number" :length="total_page_count" :total-visible="7"
      class="custome-pagination bg-transparent" @input="getInstallmentList(filter_status, true)"></v-pagination>
  </div>
</template>
<script>
import {
  getDefaultSignupDateRangeIso,
  formatSignupDateRangeDisplay,
} from "@/utils/defaultSignupDateRange";
import { mapGetters } from "vuex";
import fmt from "@/app-js/karrofin/format";
import FilterBar from "@/components/karrofin/FilterBar.vue";
import DateField from "@/components/karrofin/DateField.vue";

/**
 * Every filter this screen has, in the order it appears -- most-used first.
 * The three overdue-only controls are gated on the active KPI tab, so they
 * drop out entirely on Due and Paid rather than sitting there doing nothing.
 */
const FILTER_ORDER = [
  { key: "daterange", label: "Date range" },
  {
    key: "follow_up_date",
    label: "Follow-up date",
    show: (vm) => vm.filter_status == vm.$keys.INSTALLMENT_OVERDUE,
  },
  {
    key: "call_tag",
    label: "Call tag",
    show: (vm) => vm.filter_status == vm.$keys.INSTALLMENT_OVERDUE,
  },
  {
    key: "date_bucket_id",
    label: "Overdue bucket",
    show: (vm) => vm.filter_status == vm.$keys.INSTALLMENT_OVERDUE,
  },
  { key: "agent_type", label: "Agent type" },
  { key: "agent_assigned", label: "Agent", show: (vm) => !!vm.agent_type },
];

/** Filters shown before the "More filters" toggle -- one full grid row. */
const PRIMARY_SLOTS = 4;

export default {
  components: {
    FilterBar,
    DateField,
    FilterTab: () => import("@/components/emi-analysis/FilterTab"),
    OverDueEmiTable: () => import("@/components/emi-analysis/OverDueEmiTable"),
    UpcomingEmiTable: () =>
      import("@/components/emi-analysis/UpcomingEmiTable"),
    PaidEmiTable: () => import("@/components/emi-analysis/PaidEmiTable"),
  },
  data() {
    return {
      table_loading: false,
      page_number: 1,
      total_page_count: 2,
      step: 1,
      installment_list: [],
      total_over_due_emi_count: 0,
      total_due_emi_count: 0,
      total_paid_emi_count: 0,
      filter_status: "Overdue",

      total_paid_amount: 0,
      paid_charge: 0,
      paid_amount: 0,
      overdue_charge: 0,
      overdue_amount: 0,
      total_overdue: 0,
      upcoming_amount: 0,
      total_overdue_user_count: 0,
      total_due_user_count: 0,
      total_paid_user_count: 0,

      filter: {
        daterange: getDefaultSignupDateRangeIso(),
        display_daterange: formatSignupDateRangeDisplay(getDefaultSignupDateRangeIso()),
        fd_display_date: null,
        follow_up_date: null,
        call_tag: null,
        date_bucket_id: null,
        today: new Date().toISOString().substr(0, 10),
      },
      list_of_tags: [],
      overdue_date_list: [],

      agent_list: [],
      agent_loading: false,
      agent_page: 1,
      agent_search: "",
      agent: "",
      agent_type: "",
      agent_type_list: [
        {
          id: 1,
          text: "Assigned",
          value: true,
        },
        {
          id: 2,
          text: "Unassigned",
          value: false,
        },
      ],
    };
  },
  destroyed() {
    this.filters.agent_assigned = "";
  },
  computed: {
    ...mapGetters({
      search_query: "getSearchText",
      filters: "loan_applied/getFilters",
    }),
    /** The filters this tab actually shows, with the overflow tagged. */
    filterLayout() {
      const shown = FILTER_ORDER.filter((f) => !f.show || f.show(this));
      return shown.map((f, i) => ({ ...f, rest: i >= PRIMARY_SLOTS }));
    },
    /** How many are hidden -- drives the More/Less button and its badge. */
    restCount() {
      return this.filterLayout.filter((f) => f.rest).length;
    },
    /**
     * One entry per applied filter, for the chip row -- the only thing on the
     * page that says what is filtering the list while the grid is collapsed.
     */
    activeChips() {
      const chips = [];
      const push = (key, label, value) => chips.push({ key, label, value });
      const r = this.filter.daterange;
      if (Array.isArray(r) && r.length === 2 && r[0] && r[1]) {
        push("daterange", "Date range", fmt.dateRangeShort(r[0], r[1]));
      }
      if (this.filter.follow_up_date) {
        push("follow_up_date", "Follow-up date", this.filter.follow_up_date);
      }
      if (this.filter.call_tag) {
        push("call_tag", "Call tag", this.filter.call_tag);
      }
      if (this.filter.date_bucket_id) {
        const match = this.overdue_date_list.find(
          (d) => d.id === this.filter.date_bucket_id
        );
        push(
          "date_bucket_id",
          "Overdue bucket",
          match ? match.days_bucket_list : this.filter.date_bucket_id
        );
      }
      // Unassigned is `false`, a real selection -- a truthiness test would drop
      // its chip and leave the filter applied with nothing saying so.
      if (this.agent_type !== "" && this.agent_type !== null && this.agent_type !== undefined) {
        const match = this.agent_type_list.find((a) => a.value === this.agent_type);
        push("agent_type", "Agent type", match ? match.text : String(this.agent_type));
      }
      if (this.filters.agent_assigned && this.filters.agent_assigned.name) {
        push("agent_assigned", "Agent", this.filters.agent_assigned.name);
      }
      return chips;
    },
    itemsWithSno() {
      return this.installment_list.map((d, index) => ({
        ...d,
        sno: index + 1,
      }));
    },
  },
  watch: {
    search_query(new_search_query, old_search_query) {
      this.getInstallmentList(this.filter_status, true);
    },
    agent_type(value) {
      if (!value) {
        this.filters.agent_assigned = ""
        this.getInstallmentList(this.filter_status, true);
      }
    },
  },
  created() {
    this.getInstallmentList(this.filter_status, true);
  },
  methods: {
    /** Keeps the display labels in step with the raw dates. */
    onDateChange() {
      if (this.filter.daterange && this.filter.daterange.length > 1) {
        this.filter.display_daterange = this.formateDisplayDateRange(
          this.filter.daterange
        );
      }
      if (this.filter.follow_up_date) {
        this.filter.fd_display_date = this.display_date_formate(
          this.filter.follow_up_date
        );
      }
      this.getInstallmentList(this.filter_status, true);
    },
    /** One chip's x. Clearing Agent type also clears the agent it revealed. */
    clearFilter(key) {
      if (key === "daterange") {
        this.filter.daterange = [];
        this.filter.display_daterange = null;
      } else if (key === "follow_up_date") {
        this.filter.follow_up_date = null;
        this.filter.fd_display_date = null;
      } else if (key === "agent_type") {
        this.agent_type = "";
        this.filters.agent_assigned = "";
      } else if (key === "agent_assigned") {
        this.filters.agent_assigned = "";
      } else {
        this.filter[key] = null;
      }
      this.getInstallmentList(this.filter_status, true);
    },
    /** Clears every filter, including the ones behind the toggle. */
    resetFilters() {
      this.filter.daterange = getDefaultSignupDateRangeIso();
      this.filter.display_daterange = formatSignupDateRangeDisplay(this.filter.daterange);
      this.filter.follow_up_date = null;
      this.filter.fd_display_date = null;
      this.filter.call_tag = null;
      this.filter.date_bucket_id = null;
      this.agent_type = "";
      this.filters.agent_assigned = "";
      this.getInstallmentList(this.filter_status, true);
    },
    updateFilter(filter_status, step) {
      this.filter_status = filter_status;
      this.step = step;
      this.getInstallmentList(filter_status, true);
    },
    endIntersectAgent(entries, observer, isIntersectingAgent) {
      if (!isIntersectingAgent) return;
      // Three guards, and all three are load-bearing.
      //
      // Without the page increment (it was commented out) this fetched page 1,
      // REPLACED the list, re-rendered the menu, which recreated the sentinel,
      // which fired the observer again -- an endless request loop for as long
      // as the dropdown stayed open.
      //
      // agent_loading stops a second request while one is in flight; the menu
      // can intersect several times before the first response lands.
      //
      // agent_total_pages stops us walking past the end, which otherwise fires
      // one doomed request for page N+1 every time the user reaches the bottom.
      if (this.agent_loading) return;
      if (this.agent_page >= this.agent_total_pages) return;
      this.agent_page++;
      this.getAgentList(true);
    },
    /* fetching the customer list form server*/
    getInstallmentList(filter_status = "Overdue", isCancel = false) {
      const self = this;
      self.getOverdueEmiAmount();
      this.installment_list = [];
      console.log(filter_status);
      self.table_loading = true;
      self.toggle_loading();
      // if (self.filters.agent_assigned) {
      //   var agent_search_query = this.agent_type ? self.filters.agent_assigned.name: "";
      // }
      let params = {
        page_number: this.page_number,
        page_length: this.$keys.PAGE_LENGTH,
        search_query: this.search_query,
        installment_status: filter_status,
        call_tag: "",
        date_bucket_id: null,
        is_assigned: this.agent_type,
      };
      if (self.filters.agent_assigned && this.agent_type) {
        params.agent_search_query = self.filters.agent_assigned.name;
      }

      if (this.filter.call_tag) {
        params.call_tag = this.filter.call_tag;
      }
      if (this.filter.date_bucket_id) {
        params.date_bucket_id = this.filter.date_bucket_id;
      }
      if (this.filter.daterange) {
        params.from_date = this.filter.daterange[0];
        params.to_date = this.filter.daterange[1];
      }
      if (this.filter.follow_up_date)
        params.follow_up_date = this.filter.follow_up_date;
      console.log(params);
      const successHandler = (response) => {
        self.table_loading = false;
        if (response.data.success) {
          self.installment_list = response.data.installment_list;
          self.total_page_count = response.data.total_page_count;
          self.total_over_due_emi_count =
            response.data.total_over_due_emi_count;
          self.total_due_emi_count = response.data.total_due_emi_count;
          self.total_paid_emi_count = response.data.total_paid_emi_count;

          self.total_overdue_user_count =
            response.data.total_overdue_user_count;
          self.total_due_user_count = response.data.total_due_user_count;
          self.total_paid_user_count = response.data.total_paid_user_count;
        }
      };
      const finallyHandler = () => {
        self.table_loading = false;
        // self.toggle_loading()
      };

      self.request_GET(
        self,
        self.$urls.INSTALLMENT_LIST,
        params,
        successHandler,
        null,
        null,
        finallyHandler,
        isCancel
      );
    },
    /* fetching the amount details of overdue, due and paid emi form server*/
    getOverdueEmiAmount() {
      const self = this;

      const successHandler = (response) => {
        // if (response.data.success) {
        self.total_paid_amount = response.data.total_paid_amount_v2;
        self.paid_charge = response.data.paid_charges_v2;
        self.paid_amount = response.data.paid_amount;
        self.overdue_charge = response.data.overdue_charge;
        self.overdue_amount = response.data.overdue_amount;
        self.total_overdue = response.data.total_overdue;
        self.upcoming_amount = response.data.upcoming_amount;
        // }
      };

      self.request_GET(
        self,
        self.$urls.GET_EMI_AMOUNT_DETAILS,
        null,
        successHandler,
        null,
        null,
        null
      );
    },
    formateDisplayDateRange(daterange) {
      console.log(daterange);
      let date1 = daterange[0];
      let date2 = daterange[1];
      let to = "";
      let from = "";
      if (date1 > date2) {
        to = date1;
        from = date2;
      } else {
        to = date2;
        from = date1;
      }
      console.log(from);
      if (!to) to = from;
      daterange[0] = from;
      daterange[1] = to;
      from = this.display_date_formate(from);
      to = this.display_date_formate(to);
      return `${from} to ${to}`;
    },
    // to fetch call tag list
    getListOfTags() {
      const self = this;
      const successHandler = (response) => {
        if (response.data.success) {
          self.list_of_tags = response.data.tags[0];
        }
      };
      self.request_GET(
        self,
        self.$urls.CALL_TAG_LIST,
        null,
        successHandler,
        null,
        null,
        null
      );
    },
    getOverdueDateList() {
      const self = this;
      let look_up_key = "";
      let params = {};
      const successHandler = (response) => {
        self.overdue_date_list = response.data.result;
      };
      const finallyHandler = () => { };
      self.GET_request(
        self,
        self.$urls.OVERDUE_DATE_LIST,
        look_up_key,
        params,
        successHandler,
        null,
        null,
        finallyHandler
      );
    },
    /**
     * Debounced lookup: this was bound straight to @keyup, firing one
     * request per keystroke. 350 ms matches the interval used by FilterBar
     * and the other list screens.
     */
    onAgentSearch() {
      clearTimeout(this._agentLookupTimer);
      this._agentLookupTimer = setTimeout(() => this.getAgentList(), 350);
    },
    getAgentList(append_agent = false) {
      const self = this;
      // A fresh open or a new search term starts again at page 1. Without this
      // the page number left over from the last scroll is sent with the new
      // search, so the first response is page 3 of a list nobody has seen page
      // 1 of.
      if (!append_agent) {
        self.agent_page = 1;
      }
      self.agent_loading = true;
      let params = {
        search_query: this.agent_search,
        page_number: this.agent_page,
      };
      // console.log(params);
      const successHandler = (response) => {
        const result = (response.data && response.data.result) || {};
        const rows = result.user_data || [];
        self.agent_total_pages = result.total_page_count || 1;
        if (append_agent) {
          // Append, so scrolling adds to the list instead of swapping it out
          // underneath the user. "All" is NOT re-added here -- it goes on the
          // fresh page below, and adding it per page would scatter it through
          // the dropdown once per scroll.
          self.agent_list = self.agent_list.concat(rows);
        } else {
          self.agent_list = [{ name: "All" }].concat(rows);
        }
      };
      const finallyHandler = () => {
        self.agent_loading = false;
      };

      self.request_GET(
        self,
        self.$urls.TEAM_LIST,
        params,
        successHandler,
        null,
        null,
        finallyHandler
      );
    },
  },
};
</script>
